import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  getStockAnalyses,
  getStockAnalysisById,
  createStockAnalysis,
  updateStockAnalysis,
  deleteStockAnalysis,
  searchStocks,
  createCombinedStockAnalysis,
  getStockSectors,
  getLatestStockAnalyses,
  getSignalStats,
} from "./stock-analysis.api";
import type {
  GetStockAnalysesQuery,
  GetStockAnalysesResponse,
  CreateStockAnalysisRequest,
  UpdateStockAnalysisRequest,
  CreateCombinedStockAnalysisRequest,
  GetLatestStockAnalysesResponse,
  SignalStatsWindow,
} from "./stock-analysis.type";

// Query keys
export const stockAnalysisKeys = {
  all: ["stock-analyses"] as const,
  lists: () => [...stockAnalysisKeys.all, "list"] as const,
  list: (query: GetStockAnalysesQuery) =>
    [...stockAnalysisKeys.lists(), query] as const,
  details: () => [...stockAnalysisKeys.all, "detail"] as const,
  detail: (id: string) => [...stockAnalysisKeys.details(), id] as const,
  stats: (window: SignalStatsWindow) =>
    [...stockAnalysisKeys.all, "stats", window] as const,
};

// Get stock analyses list query
export const useStockAnalyses = (
  query: GetStockAnalysesQuery = {},
  options?: Omit<
    UseQueryOptions<GetStockAnalysesResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: stockAnalysisKeys.list(query),
    queryFn: () => getStockAnalyses(query),
    placeholderData: keepPreviousData,
    ...options,
  });
};

// Get stock analysis by ID query
export const useStockAnalysis = (id: string) => {
  return useQuery({
    queryKey: stockAnalysisKeys.detail(id),
    queryFn: () => getStockAnalysisById(id),
    enabled: !!id, // Only run if id is truthy
  });
};

// Create stock analysis mutation
export const useCreateStockAnalysis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStockAnalysisRequest) => createStockAnalysis(data),
    onSuccess: () => {
      // Invalidate stock analyses list queries
      queryClient.invalidateQueries({ queryKey: stockAnalysisKeys.lists() });
    },
    onError: (error) => {
      console.error("Create stock analysis failed:", error);
    },
  });
};

// Update stock analysis mutation
export const useUpdateStockAnalysis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateStockAnalysisRequest;
    }) => updateStockAnalysis({ id, data }),
    onSuccess: (_, variables) => {
      // Invalidate specific stock analysis detail
      queryClient.invalidateQueries({
        queryKey: stockAnalysisKeys.detail(variables.id),
      });
      // Invalidate stock analyses list queries
      queryClient.invalidateQueries({ queryKey: stockAnalysisKeys.lists() });
    },
    onError: (error) => {
      console.error("Update stock analysis failed:", error);
    },
  });
};

// Delete stock analysis mutation
export const useDeleteStockAnalysis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteStockAnalysis(id),
    onSuccess: () => {
      // Invalidate stock analyses list queries
      queryClient.invalidateQueries({ queryKey: stockAnalysisKeys.lists() });
    },
    onError: (error) => {
      console.error("Delete stock analysis failed:", error);
    },
  });
};

// Search stocks query
export const useSearchStocks = (query: string) => {
  return useQuery({
    queryKey: ["stocks", "search", query],
    queryFn: () => searchStocks(query),
    enabled: query.length > 0, // Only run if query has content
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

// Create combined stock analysis and report mutation
export const useCreateCombinedStockAnalysis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCombinedStockAnalysisRequest) =>
      createCombinedStockAnalysis(data),
    onSuccess: () => {
      // Invalidate stock analyses list queries
      queryClient.invalidateQueries({ queryKey: stockAnalysisKeys.lists() });
    },
    onError: (error) => {
      console.error("Create combined stock analysis failed:", error);
    },
  });
};

// Get stock sectors query
export const useStockSectors = () => {
  return useQuery({
    queryKey: ["stock-analyses", "sectors"],
    queryFn: () => getStockSectors(),
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
};

// Get latest stock analyses query (public endpoint)
export const useLatestStockAnalyses = (
  options?: Omit<
    UseQueryOptions<GetLatestStockAnalysesResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["stock-analyses", "latest", "public"],
    queryFn: () => getLatestStockAnalyses(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    ...options,
  });
};

// Signal track record stats (public endpoint)
export const useSignalStats = (window: SignalStatsWindow = "12m") => {
  return useQuery({
    queryKey: stockAnalysisKeys.stats(window),
    queryFn: () => getSignalStats(window),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};
